import { NgClass } from '@angular/common';
import type { ElementRef, OnInit } from '@angular/core';
import {
    booleanAttribute,
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    output,
    signal,
    viewChild,
    ViewEncapsulation,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { BytesPipe, EditApiService } from '@hyundai/ng-common-lib';
import type { Identifiers } from '@hyundai/shared-lib/edit';
import { getEditMasksImageRequirements, validateImageRequirements } from '@hyundai/shared-lib/edit';
import { M000C010ImageComponent, M000C040LoadingSpinnerComponent } from '@hyundai/ui';

@Component({
    selector: 'lib-image-dropper',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        BytesPipe,
        NgClass,
        M000C010ImageComponent,
        M000C040LoadingSpinnerComponent,
        M000C040LoadingSpinnerComponent,
    ],
    templateUrl: './image-dropper.component.html',
    styleUrl: './image-dropper.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImageDropperComponent implements OnInit {
    protected api = inject(EditApiService);
    protected fileInput = viewChild<ElementRef>('fileInput');

    identifier = input.required<Identifiers>();
    image = input<string | null>(null);
    publicId = output<string>();
    isRequired = input(false, { transform: booleanAttribute });
    previewImage = signal<string | null>(null);
    error = input(false, { transform: booleanAttribute });
    withAspectRatio = input(true, { transform: booleanAttribute });

    showLoadingSpinner = signal(false);

    requirements = computed(() => {
        const identifier = this.identifier();
        return getEditMasksImageRequirements(identifier);
    });

    allowedFileTypes = computed(() => {
        const reqs = this.requirements();
        if (!reqs || !reqs.types || reqs.types.length === 0) return [];
        return reqs.types.map((t) => `image/${t === 'jpg' ? 'jpeg' : t}`);
    });

    acceptTypes = computed(() => {
        const types = this.allowedFileTypes();
        if (types.length === 0) return 'image/*';
        return types.join(', ');
    });

    ngOnInit() {
        this.previewImage.set(this.image());
    }

    async getImageDimensions(file: File): Promise<{ width: number; height: number }> {
        const url = URL.createObjectURL(file);
        try {
            const img = new Image();
            // eslint-disable-next-line unicorn/prefer-add-event-listener
            const loaded = new Promise((resolve) => (img.onerror = img.onload = resolve));
            img.src = url;
            await loaded;
            return { width: img.width, height: img.height };
        } finally {
            URL.revokeObjectURL(url);
        }
    }

    // eslint-disable-next-line max-statements
    async fileChangeHandler(event: Event) {
        const target = event.target as HTMLInputElement;
        const file = target.files?.item(0);

        if (!file) return;
        this.showLoadingSpinner.set(true);
        try {
            const dimensions = await this.getImageDimensions(file);
            await validateImageRequirements(this.identifier(), file, {
                width: dimensions.width,
                height: dimensions.height,
            });
        } catch (error) {
            if (error instanceof Error) {
                alert(error.message);
            }
            this.showLoadingSpinner.set(false);
            this.remove();
            return;
        }
        try {
            const resp = await this.api.postTmpImage(this.identifier(), file);
            this.previewImage.set(resp.url);
            this.publicId.emit(resp.publicId);
        } catch (error) {
            console.error(error);
        }
        this.showLoadingSpinner.set(false);
    }

    remove(): void {
        (this.fileInput()?.nativeElement as HTMLInputElement).value = '';
        this.previewImage.set(null);
        this.publicId.emit('');
    }
}
